/**
 * renderers-logic.ts — pure (DOM-free) helpers extracted from renderers.ts.
 *
 * These functions have no window/document/sessionStorage dependency and are
 * importable in Node.js vitest without any stubs. Each function is exported
 * byte-identically from the original renderers.ts — this is extraction, not
 * rewrite. renderers.ts re-imports all of these from this module.
 *
 * WHY a separate module? Seam-based unit testing: vitest instruments the .ts
 * source via resolve.alias (vitest.config.ts spa-seam project) so coverage
 * numbers are honest. DOM-touching functions stay in renderers.ts and remain
 * covered only by the full-stack seam test (test/renderers.seam.test.ts).
 *
 * See docs/epics/active/ts-unit-testability-seams-260623/main.md and the
 * W-J wave (examples-format.ts) for the canonical extraction pattern.
 */
// ─── Internal helpers ──────────────────────────────────────────────────────
/**
 * Pure HTML-escape helper (no DOM; identical algorithm to web/static/esc.ts
 * `adaEsc`). Used by renderReport so the function stays self-contained and
 * testable without window.adatool.esc being set.
 */
function _esc(s) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- s is intentionally unknown (generic HTML-escape helper); String() is the explicit safe coercion
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
// ─── fileIcon ──────────────────────────────────────────────────────────────
export function fileIcon(filename) {
    const ext = (filename ?? '').toLowerCase();
    if (ext.endsWith('.docx') || ext.endsWith('.doc'))
        return '<svg width="18" height="18" aria-hidden="true" class="file-icon"><use href="#icon-docx"/></svg>';
    if (ext.endsWith('.xlsx') || ext.endsWith('.xls'))
        return '<svg width="18" height="18" aria-hidden="true" class="file-icon"><use href="#icon-xlsx"/></svg>';
    if (ext.endsWith('.pdf'))
        return '<svg width="18" height="18" aria-hidden="true" class="file-icon"><use href="#icon-pdf"/></svg>';
    // .odp — OpenDocument Presentation (libreoffice-odf Phase 3). Maps to the
    // dedicated glyph so a .odp in the job list renders as ODF, not the pptx
    // fallback.
    if (ext.endsWith('.odp'))
        return '<svg width="18" height="18" aria-hidden="true" class="file-icon"><use href="#icon-odp"/></svg>';
    return '<svg width="18" height="18" aria-hidden="true" class="file-icon"><use href="#icon-pptx"/></svg>';
}
// ─── formatFileSize ────────────────────────────────────────────────────────
export function formatFileSize(bytes) {
    if (typeof bytes !== 'number' || bytes < 0)
        return '';
    if (bytes < 1024)
        return bytes + ' B';
    if (bytes < 1024 * 1024)
        return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024)
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}
// ─── timeSince ─────────────────────────────────────────────────────────────
export function timeSince(start, end) {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const sec = Math.round(ms / 1000);
    if (sec < 60)
        return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}
// ─── friendlyIssueLabel ────────────────────────────────────────────────────
// Checker produces machine-friendly rule IDs ("PDF/UA-1.6.1.1-1") and prose
// descriptions; we prefer the description but fall back to the rule ID.
// We also normalize a handful of common rule IDs into actionable phrasing
// so users see "1 image still missing alt text" rather than the raw rule.
const _KNOWN_ISSUE_LABELS = [
    ['alt-text-missing', 'Image is missing alt text'],
    ['figure-no-alt', 'Figure is missing alt text'],
    ['heading-skip', 'Heading hierarchy skips a level'],
    ['heading-no-h1', 'Document has no top-level heading (H1)'],
    ['table-no-header', 'Table is missing header row'],
    ['table-header-missing', 'Table is missing header row'],
    ['list-malformed', 'List structure is malformed'],
    ['contrast-low', 'Text contrast is too low'],
    // Namespace-agnostic Office coverage (office-friendly-label-namespace-coverage
    // follow-up, thinky-web-altcount-surface-260816 RCA §5). The Office checker's
    // real rule ids are `OFFICE-<FMT>-<CAT>-NN` — before this block only the
    // `-contrast` entry (below) was normalized; every other Office category fell
    // through to the raw verbose checker description. ORDER MATTERS: a specific
    // sub-variant entry MUST precede its generic sibling (first `includes` match
    // wins), else e.g. a "trivial alt text" issue would mislabel as "missing".
    // Alt-text sub-variants (Picture/Drawing) — specific before generic 'alt-01'.
    ['alt-explicit-empty', 'Alt text is explicitly marked empty (decorative)'],
    ['alt-fallback', 'Alt text is placeholder/fallback text, not a real description'],
    ['alt-short', 'Alt text is too short to be descriptive'],
    ['alt-trivial', 'Alt text is trivial (e.g. a filename or generic word)'],
    ['alt-language-mismatch', 'Alt text language does not match the document language'],
    ['alt-01', 'Alt text is missing'],
    // Title sub-variants (Slide/Presentation/Document/Workbook) — the path and
    // duplicate variants must precede the generic missing-title entries.
    ['slide-title-dup', "Slide title duplicates another slide's title"],
    ['title-path', 'Title is a raw file path, not a descriptive title'],
    ['slide-title', 'Slide is missing a title'],
    ['pres-title', 'Presentation title is missing'],
    ['doc-title', 'Document title is missing'],
    ['workbook-title', 'Workbook title is missing'],
    // Heading structure.
    ['heading-empty', 'Heading paragraph is empty'],
    ['long-doc-no-headings', 'Long document has no heading structure'],
    // Table.
    ['table-header', 'Table is missing a header row'],
    ['table-cell', 'Table header cells are inconsistently marked'],
    // Links.
    ['link-text-raw-url', 'Link text is the raw URL, not descriptive text'],
    ['link-text-vague', 'Link text is vague (e.g. "click here")'],
    ['link-target-malformed', 'Link target is broken or malformed'],
    // Language.
    ['doc-lang', 'Document language is not set'],
    ['cell-lang-parts-mismatch', 'Cell language does not match its content'],
    // Media / captions.
    ['media-caption', 'Media is missing a caption'],
    ['caption-quality', 'Caption text quality is low'],
    // Namespace-agnostic contrast normalization. The Office checker's real rule
    // ids are `OFFICE-<FMT>-CONTRAST-<KIND>-NN` (e.g. OFFICE-PPTX-CONTRAST-TEXT-01,
    // -CONTRAST-NONTEXT-01, -CONTRAST-AUTOSHAPE-01, -CONTRAST-CELL-01 — all four
    // KIND variants), which contain neither `contrast-low` nor any alt/figure
    // token — without this `-contrast` `includes` match they fell through to the
    // verbose checker description, inviting a residual contrast warning on a
    // figure-dense deck to be misread as an alt-text problem
    // (thinky-web-altcount-surface-260816 RCA). Deliberately ONE label across all
    // KIND variants (not split per-kind) — this is the pre-existing, pinned
    // contrast-never-alt regression guard; splitting it is out of scope here.
    ['-contrast', 'Text contrast is too low'],
    ['link-no-text', 'Link has no descriptive text'],
    ['lang-missing', 'Document language is not set'],
    ['title-missing', 'Document title is missing'],
    ['reading-order', 'Reading order may be incorrect'],
    ['matterhorn-', 'PDF/UA structure issue'],
    ['pdfua-', 'PDF/UA tag issue'],
    // Misc Office categories — 'nontext' does not collide with '-contrast' above
    // (OFFICE-DOCX-NONTEXT-01 contains no "contrast" substring).
    ['color-only-info', 'Information is conveyed by color alone'],
    ['auto-advance', 'Slide auto-advances without a pause control'],
    ['form-field-no-label', 'Form field has no label'],
    ['nontext', 'Picture has neither alt text nor a caption'],
    ['sheet-name', 'Sheet name is generic, not descriptive'],
];
export function friendlyIssueLabel(issue) {
    if (!issue)
        return 'Issue found';
    const ridLower = String(issue.rule_id ?? '').toLowerCase();
    // Known mappings — extend as the checker surfaces more rule IDs in
    // prod. The first matching prefix wins.
    for (const [prefix, friendly] of _KNOWN_ISSUE_LABELS) {
        if (ridLower.startsWith(prefix) || ridLower.includes(prefix))
            return friendly;
    }
    // Otherwise fall back to the checker-provided description, then the
    // rule_id, then a generic placeholder.
    const desc = (issue.description ?? '').trim();
    if (desc)
        return desc;
    if (issue.rule_id)
        return String(issue.rule_id);
    return 'Issue found';
}
// ─── renderReport (legacy report block) ────────────────────────────────────
export function renderReport(report) {
    if (!report)
        return '';
    const errors = report.errors ?? [];
    const warnings = report.warnings ?? [];
    if (errors.length === 0 && warnings.length === 0) {
        return '<div class="report-inline report-clean">All accessibility checks passed</div>';
    }
    let html = '<div class="report-inline report-issues">';
    if (errors.length > 0) {
        const totalE = report.total_errors ?? errors.length;
        html += '<div class="report-section">';
        html += '<div class="report-section-title errors">Errors (' + totalE + ')</div>';
        for (const e of errors) {
            const label = e.slide ? `Slide ${e.slide}: ${_esc(e.issue)}` :
                e.count ? `${_esc(e.issue)} (${e.count})` : _esc(e.issue);
            html += `<div class="report-item">${label}</div>`;
        }
        html += '</div>';
    }
    if (warnings.length > 0) {
        const totalW = report.total_warnings ?? warnings.length;
        html += '<div class="report-section">';
        html += '<div class="report-section-title advisory">Advisory (' + totalW + ')</div>';
        for (const w of warnings) {
            const label = w.slide ? `Slide ${w.slide}: ${_esc(w.issue)}` :
                w.count ? `${_esc(w.issue)} (${w.count})` : _esc(w.issue);
            html += `<div class="report-item">${label}</div>`;
        }
        html += '</div>';
    }
    html += '</div>';
    return html;
}
